import React, { JSX, Suspense } from 'react';
import { StudioHeader } from '@vp/diy-editor-ui';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useBookendHeaderData } from 'src/hooks/useBookendHeaderData';
import {
  HeaderLeadingSkeleton,
  HeaderLogoMobileSkeleton,
  HeaderSkeleton,
  HeaderTrailingSkeleton,
} from './HeaderSkeleton';
import { HeaderInfoProvider } from '../HeaderInfoProvider';
import './header.css';

// Lazy load heavy / secondary header child nodes so they don't block initial paint.
// Each is code-split into its own chunk; adjust chunk names if you prefer grouping.
const VistaLogoLazy = React.lazy(() =>
  import(/* webpackChunkName: "VistaLogo" */ './VistaLogo').then(mod => ({ default: mod.VistaLogo }))
);
const HeaderRightChildrenLazy = React.lazy(() =>
  import(/* webpackChunkName: "HeaderRightChildren" */ './HeaderRightChildren').then(mod => ({
    default: mod.HeaderRightChildren,
  }))
);
const HeaderMobileLeftChildrenLazy = React.lazy(() =>
  import(/* webpackChunkName: "HeaderMobileLeftChildren" */ './HeaderMobileLeftChildren').then(mod => ({
    default: mod.HeaderMobileLeftChildren,
  }))
);

export const Header = (): JSX.Element => {
  const bookEndHeaderData = useBookendHeaderData();
  const { completedProductInformation } = useProductContext();

  // UI Seems to jump on initial load as bookEndHeaderData is undefined
  // Hence returning HeaderSkeleton so show loading state
  if (!bookEndHeaderData || !completedProductInformation) {
    return <HeaderSkeleton />;
  }

  return (
    <HeaderInfoProvider headerData={bookEndHeaderData}>
      <StudioHeader
        px={{ xs: 5, sm: 6 }}
        py={{ xs: 3, sm: 5 }}
        className={'header-style'}
        leadingNode={
          <Suspense fallback={<HeaderLeadingSkeleton />}>
            <VistaLogoLazy />
          </Suspense>
        }
        trailingNode={
          <Suspense fallback={<HeaderTrailingSkeleton />}>
            <HeaderRightChildrenLazy />
          </Suspense>
        }
        leadingNodeSmallScreen={
          <Suspense fallback={<></>}>
            <HeaderMobileLeftChildrenLazy />
          </Suspense>
        }
        logoSmallScreen={
          <Suspense fallback={<HeaderLogoMobileSkeleton />}>
            <VistaLogoLazy />
          </Suspense>
        }
      />
    </HeaderInfoProvider>
  );
};
